import { useCallback, useEffect, useMemo, useState } from "react";
import { FileUp, Loader2, Pencil, Plus, Trash2, X, FileText } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyRow = { name: "", unit: "", ref_low: "", ref_high: "", critical_low: "", critical_high: "", delta_limit: "", delta_type: "percent" };
const numOrNull = (v) => (String(v).trim() === "" ? null : Number(v));

export default function Templates() {
  const { options } = useAuth();
  const [panels, setPanels] = useState([]);
  const [uploading, setUploading] = useState("");
  const [editing, setEditing] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/panels");
      setPanels(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tests = useMemo(() => {
    const fromOptions = options.test_name || [];
    const extra = panels.map((p) => p.test_name).filter((t) => !fromOptions.includes(t));
    return [...fromOptions, ...extra].sort();
  }, [options, panels]);

  const panelOf = (test) => panels.find((p) => p.test_name === test);

  const upload = async (test, file) => {
    if (!file) return;
    setUploading(test);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/panels/${encodeURIComponent(test)}/template`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.parameters.length} parameters read from ${file.name}`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(""); }
  };

  const removeSource = async (test) => {
    try {
      await api.delete(`/panels/${encodeURIComponent(test)}/source`);
      toast.success("Uploaded template removed — parameters kept");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const clearPanel = async (test) => {
    if (!window.confirm(`Remove the whole result template for ${test}?`)) return;
    try {
      await api.delete(`/panels/${encodeURIComponent(test)}`);
      toast.success("Template removed");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openEdit = (test) => {
    const p = panelOf(test);
    setRows((p?.parameters || []).map((x) => ({
      name: x.name, unit: x.unit || "",
      ref_low: x.ref_low ?? "", ref_high: x.ref_high ?? "",
      critical_low: x.critical_low ?? "", critical_high: x.critical_high ?? "",
      delta_limit: x.delta_limit ?? "", delta_type: x.delta_type || "percent",
    })));
    setEditing(test);
  };

  const savePanel = async () => {
    setSaving(true);
    try {
      await api.put(`/panels/${encodeURIComponent(editing)}`, {
        parameters: rows.filter((r) => r.name.trim()).map((r) => ({
          name: r.name.trim(), unit: r.unit,
          ref_low: numOrNull(r.ref_low), ref_high: numOrNull(r.ref_high),
          critical_low: numOrNull(r.critical_low), critical_high: numOrNull(r.critical_high),
          delta_limit: numOrNull(r.delta_limit), delta_type: r.delta_type || "percent",
        })),
      });
      toast.success("Template saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const setRow = (i, key) => (e) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: e.target.value } : r)));

  return (
    <div data-testid="templates-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">
        Test Templates
      </h1>
      <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
        Upload one of your own past reports for each test and LabTrack reads its parameter list, units,
        reference ranges and critical limits — that template then appears in the sample entry form whenever the
        test is selected. Everything stays editable, and any upload can be removed and replaced.
      </p>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
        {tests.map((test) => {
          const p = panelOf(test);
          const count = p?.parameters?.length || 0;
          return (
            <div key={test} className="rounded-md border border-[#E5E7EB] bg-white p-4"
              data-testid={`template-card-${test}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-heading font-semibold text-sm text-[#111827]">{test}</h2>
                  <p className="text-xs text-[#6B7280] mt-0.5" data-testid={`template-count-${test}`}>
                    {count ? `${count} parameters` : "No template yet"}
                  </p>
                </div>
                {count > 0 && (
                  <button onClick={() => clearPanel(test)} data-testid={`template-clear-${test}`}
                    title="Remove template" className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {count > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {p.parameters.slice(0, 6).map((x) => (
                    <span key={x.name}
                      className="rounded-full bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5 text-[11px] text-[#111827]">
                      {x.name}
                    </span>
                  ))}
                  {count > 6 && <span className="text-[11px] text-[#6B7280]">+{count - 6} more</span>}
                </div>
              )}

              {p?.source?.file_id && (
                <div className="mt-3 flex items-center gap-2 text-xs" data-testid={`template-source-${test}`}>
                  <FileText className="h-3.5 w-3.5 text-[#002FA7]" />
                  <a href={fileUrl(p.source.file_id)} target="_blank" rel="noreferrer"
                    className="text-[#002FA7] hover:underline truncate max-w-[160px]">{p.source.filename}</a>
                  <button onClick={() => removeSource(test)} data-testid={`template-source-remove-${test}`}
                    className="text-[#6B7280] hover:text-[#C92A2A]"><X className="h-3.5 w-3.5" /></button>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                <label
                  className="inline-flex items-center h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs cursor-pointer hover:border-[#002FA7] transition-colors"
                  data-testid={`template-upload-label-${test}`}>
                  {uploading === test
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <FileUp className="h-3.5 w-3.5 mr-1.5 text-[#002FA7]" />}
                  {uploading === test ? "Reading…" : p?.source ? "Replace upload" : "Upload past report"}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                    data-testid={`template-upload-input-${test}`}
                    onChange={(e) => { upload(test, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                <Button variant="outline" size="sm" onClick={() => openEdit(test)}
                  data-testid={`template-edit-${test}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5 text-[#002FA7]" /> Edit parameters
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{editing} — parameters</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Reference range decides the High / Low highlight; critical limits decide what raises an automatic
              critical alert; the delta limit warns when a repeat result for the same patient swings further
              than this. Leave a value blank if it does not apply.
            </DialogDescription>
          </DialogHeader>

          <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">
            <span className="col-span-3">Parameter</span><span className="col-span-1">Unit</span>
            <span className="col-span-1">Ref low</span><span className="col-span-1">Ref high</span>
            <span className="col-span-1">Crit low</span><span className="col-span-1">Crit high</span>
            <span className="col-span-1">Delta</span><span className="col-span-3">Delta basis</span>
          </div>
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`template-row-${i}`}>
                <Input value={row.name} onChange={setRow(i, "name")} placeholder="Haemoglobin"
                  className="col-span-3 h-9 text-sm" data-testid={`template-row-name-${i}`} />
                <Input value={row.unit} onChange={setRow(i, "unit")} placeholder="g/dL"
                  className="col-span-1 h-9 text-sm" data-testid={`template-row-unit-${i}`} />
                <Input value={row.ref_low} onChange={setRow(i, "ref_low")} className="col-span-1 h-9 text-sm"
                  data-testid={`template-row-reflow-${i}`} />
                <Input value={row.ref_high} onChange={setRow(i, "ref_high")} className="col-span-1 h-9 text-sm"
                  data-testid={`template-row-refhigh-${i}`} />
                <Input value={row.critical_low} onChange={setRow(i, "critical_low")} className="col-span-1 h-9 text-sm"
                  data-testid={`template-row-critlow-${i}`} />
                <Input value={row.critical_high} onChange={setRow(i, "critical_high")} className="col-span-1 h-9 text-sm"
                  data-testid={`template-row-crithigh-${i}`} />
                <Input value={row.delta_limit} onChange={setRow(i, "delta_limit")} className="col-span-1 h-9 text-sm"
                  data-testid={`template-row-delta-${i}`} />
                <div className="col-span-3 flex items-center gap-1">
                  <select value={row.delta_type || "percent"} onChange={setRow(i, "delta_type")}
                    data-testid={`template-row-deltatype-${i}`}
                    className="h-9 flex-1 rounded-md border border-[#E5E7EB] bg-white px-2 text-xs">
                    <option value="percent">% change</option>
                    <option value="absolute">absolute change</option>
                  </select>
                  <button type="button" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
                    data-testid={`template-row-remove-${i}`}
                    className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {rows.length === 0 && (
              <p className="text-sm text-[#6B7280]" data-testid="template-rows-empty">
                No parameters yet — add them one by one, or upload a past report to read them automatically.
              </p>
            )}
          </div>

          <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, { ...emptyRow }])}
            data-testid="template-add-row" className="w-fit">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add parameter
          </Button>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="template-cancel">Cancel</Button>
            <Button onClick={savePanel} disabled={saving} data-testid="template-save"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              {saving ? "Saving…" : "Save template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
